import { create } from 'zustand';
import { CAPTURE_DEFAULTS, DEFAULT_SESSION_LENGTH_MIN, SESSION_LENGTH_CHOICES_MIN } from '@erectus/shared';
import type { Calibration, SessionLengthMin } from '@erectus/shared';
import type { CameraFacing } from '@/lib/camera.js';

/**
 * Capture settings carried from the setup screen into the live session.
 * Deliberately not persisted: every session re-runs the checklist, because the
 * camera has usually moved.
 */
interface SessionState {
  sessionId: string | null;
  plannedMin: SessionLengthMin;
  sampleFps: number;
  poseModel: 'lite' | 'full';
  calibration: Calibration | null;
  dimByDefault: boolean;
  /**
   * Which camera the user framed the shot with. The session must continue on
   * the same one: switching to the back camera after someone set up on the
   * front points the phone at the wrong half of the room.
   */
  facing: CameraFacing;
  /** Play audio cues for hands-free start. */
  cues: boolean;
  setPlanned(minutes: SessionLengthMin): void;
  configure(patch: Partial<Omit<SessionState, 'configure' | 'setPlanned' | 'begin' | 'clear'>>): void;
  begin(sessionId: string): void;
  clear(): void;
}

const FACING_KEY = 'erectus.cameraFacing';

/**
 * Front camera unless the user picked the rear one last time. With the screen
 * facing you, you can read the checklist and the countdown from your chair.
 */
function storedFacing(): CameraFacing {
  try {
    return window.localStorage.getItem(FACING_KEY) === 'environment' ? 'environment' : 'user';
  } catch {
    return 'user';
  }
}

export function rememberFacing(facing: CameraFacing): void {
  try {
    window.localStorage.setItem(FACING_KEY, facing);
  } catch {
    // Remembering the camera is a convenience.
  }
}

export const useSession = create<SessionState>((set) => ({
  sessionId: null,
  plannedMin: DEFAULT_SESSION_LENGTH_MIN,
  sampleFps: CAPTURE_DEFAULTS.sampleFps,
  poseModel: CAPTURE_DEFAULTS.poseModel,
  calibration: null,
  dimByDefault: false,
  facing: storedFacing(),
  cues: true,
  setPlanned: (minutes) => set({ plannedMin: minutes }),
  configure: (patch) => set(patch as Partial<SessionState>),
  begin: (sessionId) => set({ sessionId }),
  clear: () => set({ sessionId: null, calibration: null }),
}));

export { SESSION_LENGTH_CHOICES_MIN };

export function newSessionId(): string {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
